const COLLECTION_NAMES = ['competitions', 'teams', 'players', 'matches', 'gameFacts'];

const toIdString = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'object' && value._id !== undefined) return String(value._id);
  return String(value);
};

const isMongoObjectId = (value) => /^[a-fA-F0-9]{24}$/.test(String(value || ''));

const getArray = (snapshot, key) =>
  Array.isArray(snapshot?.data?.[key]) ? snapshot.data[key] : [];

const pushIssue = (issues, severity, type, message, context = {}) => {
  issues.push({ severity, type, message, ...context });
};

export const getSnapshotCounts = (snapshot) =>
  COLLECTION_NAMES.reduce((acc, key) => {
    acc[key] = getArray(snapshot, key).length;
    return acc;
  }, {});

export const inspectSnapshot = (snapshot) => {
  const errors = [];
  const warnings = [];

  if (!snapshot || typeof snapshot !== 'object' || Array.isArray(snapshot)) {
    return {
      errors: [{ type: 'INVALID_ROOT', message: 'Le fichier JSON doit contenir un objet snapshot.' }],
      warnings,
    };
  }

  if (!snapshot.manifest || typeof snapshot.manifest !== 'object') {
    errors.push({ type: 'MISSING_MANIFEST', message: 'Le manifest du snapshot est absent.' });
  }

  if (!snapshot.data || typeof snapshot.data !== 'object') {
    errors.push({ type: 'MISSING_DATA', message: 'La section data du snapshot est absente.' });
  }

  if (!snapshot?.manifest?.sourceProvider) {
    errors.push({ type: 'MISSING_SOURCE_PROVIDER', message: 'manifest.sourceProvider est absent.' });
  }

  if (!snapshot?.manifest?.discipline) {
    errors.push({ type: 'MISSING_DISCIPLINE', message: 'manifest.discipline est absent.' });
  }

  if (Number(snapshot?.manifest?.version) !== 2) {
    errors.push({
      type: 'UNSUPPORTED_SNAPSHOT_VERSION',
      message: `Version de snapshot non supportée : ${snapshot?.manifest?.version ?? 'absente'}. Version attendue : 2.`,
    });
  }

  COLLECTION_NAMES.forEach((key) => {
    if (!Array.isArray(snapshot?.data?.[key])) {
      errors.push({
        type: 'INVALID_COLLECTION',
        message: `data.${key} doit être un tableau.`,
        collection: key,
      });
    }
  });

  if (snapshot.valid === false) {
    errors.push({
      type: 'SNAPSHOT_MARKED_INVALID',
      message: 'Le snapshot est explicitement marqué comme invalide.',
    });
  }

  if (Array.isArray(snapshot.errors) && snapshot.errors.length > 0) {
    errors.push({
      type: 'SNAPSHOT_CONTAINS_EXPORT_ERRORS',
      message: `Le snapshot contient ${snapshot.errors.length} erreur(s) d'export enregistrée(s).`,
    });
  }

  if (snapshot?.manifest?.targetProvider) {
    warnings.push({
      type: 'LEGACY_TARGET_PROVIDER',
      message: `Ce snapshot contient encore un provider cible legacy (${snapshot.manifest.targetProvider}). Il sera ignoré à l'import.`,
    });
  }

  const counts = getSnapshotCounts(snapshot);
  COLLECTION_NAMES.forEach((key) => {
    const summaryValue = snapshot?.summary?.[key];
    if (typeof summaryValue === 'number' && summaryValue !== counts[key]) {
      warnings.push({
        type: 'SUMMARY_COUNT_MISMATCH',
        message: `summary.${key} annonce ${summaryValue}, mais data.${key} contient ${counts[key]} élément(s).`,
        collection: key,
      });
    }
  });

  return { errors, warnings };
};

const findDuplicateIds = (items, entityType, issues) => {
  const seen = new Set();
  items.forEach((item) => {
    const id = toIdString(item?._id);
    if (!id) {
      pushIssue(issues, 'error', 'MISSING_MONGO_ID', `${entityType} sans _id dans le snapshot.`, {
        entityType,
      });
      return;
    }

    if (seen.has(id)) {
      pushIssue(issues, 'error', 'DUPLICATE_MONGO_ID', `${entityType} dupliqué dans le snapshot : ${id}.`, {
        entityType,
        entityId: id,
      });
    }
    seen.add(id);
  });
};

const validateIdentityCollisions = (items, entityType, sourceProvider, sourceDiscipline, issues) => {
  const buckets = new Map();

  items.forEach((item) => {
    const entityId = toIdString(item?._id);
    const provider = item?.provider;
    const thirdPartyId = item?.thirdPartyId;
    const thirdPartyEntityId = item?.thirdPartyEntityId;

    if (provider && sourceProvider && String(provider) !== String(sourceProvider)) {
      pushIssue(
        issues,
        'error',
        'SOURCE_PROVIDER_MISMATCH',
        `${entityType} ${entityId || ''} : provider=${provider} au lieu de ${sourceProvider}.`,
        { entityType, entityId }
      );
    }

    if (item?.discipline && sourceDiscipline && String(item.discipline) !== String(sourceDiscipline)) {
      pushIssue(
        issues,
        'error',
        'SOURCE_DISCIPLINE_MISMATCH',
        `${entityType} ${entityId || ''} : discipline=${item.discipline} au lieu de ${sourceDiscipline}.`,
        { entityType, entityId }
      );
    }

    if (!provider || !thirdPartyId || !thirdPartyEntityId) {
      pushIssue(
        issues,
        'error',
        'MISSING_LOGICAL_IDENTITY',
        `${entityType} ${entityId || ''} : identité provider / thirdPartyId / thirdPartyEntityId incomplète.`,
        { entityType, entityId }
      );
      return;
    }

    const key = [provider, thirdPartyId, thirdPartyEntityId].map(String).join('::');
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(entityId);
  });

  buckets.forEach((ids, key) => {
    if (ids.length <= 1) return;
    pushIssue(
      issues,
      'error',
      'IDENTITY_COLLISION',
      `Collision ${entityType} sur l'identité ${key.split('::').join(' / ')} pour ${ids.length} documents.`,
      { entityType, entityIds: ids }
    );
  });
};

export const prevalidateSnapshotLocally = (snapshot) => {
  const initial = inspectSnapshot(snapshot);
  const issues = [
    ...initial.errors.map((issue) => ({ ...issue, severity: 'error' })),
    ...initial.warnings.map((issue) => ({ ...issue, severity: 'warning' })),
  ];

  if (initial.errors.length) {
    return {
      valid: false,
      issues,
      errors: issues.filter((issue) => issue.severity === 'error'),
      warnings: issues.filter((issue) => issue.severity === 'warning'),
    };
  }

  const competitions = getArray(snapshot, 'competitions');
  const teams = getArray(snapshot, 'teams');
  const players = getArray(snapshot, 'players');
  const matches = getArray(snapshot, 'matches');
  const gameFacts = getArray(snapshot, 'gameFacts');
  const sourceProvider = snapshot?.manifest?.sourceProvider;
  const sourceDiscipline = snapshot?.manifest?.discipline;

  findDuplicateIds(competitions, 'competition', issues);
  findDuplicateIds(teams, 'team', issues);
  findDuplicateIds(players, 'player', issues);
  findDuplicateIds(matches, 'match', issues);
  findDuplicateIds(gameFacts, 'gameFact', issues);

  validateIdentityCollisions(competitions, 'competition', sourceProvider, sourceDiscipline, issues);
  validateIdentityCollisions(teams, 'team', sourceProvider, sourceDiscipline, issues);
  validateIdentityCollisions(players, 'player', sourceProvider, sourceDiscipline, issues);
  validateIdentityCollisions(matches, 'match', sourceProvider, sourceDiscipline, issues);

  const competitionIds = new Set(competitions.map((item) => toIdString(item?._id)).filter(Boolean));
  const teamIds = new Set(teams.map((item) => toIdString(item?._id)).filter(Boolean));
  const playerIds = new Set(players.map((item) => toIdString(item?._id)).filter(Boolean));
  const matchIds = new Set(matches.map((item) => toIdString(item?._id)).filter(Boolean));

  teams.forEach((team) => {
    const teamId = toIdString(team?._id);
    (Array.isArray(team?.competitions) ? team.competitions : []).forEach((competitionRef) => {
      const competitionId = toIdString(competitionRef);
      if (competitionId && !competitionIds.has(competitionId)) {
        pushIssue(
          issues,
          'error',
          'TEAM_COMPETITION_OUT_OF_SCOPE',
          `Equipe ${team?.name || teamId} : compétition ${competitionId} absente du snapshot.`,
          { entityType: 'team', entityId: teamId }
        );
      }
    });
  });

  players.forEach((player) => {
    const playerId = toIdString(player?._id);
    const playerName = [player?.firstname, player?.lastname].filter(Boolean).join(' ') || playerId;

    (Array.isArray(player?.teams) ? player.teams : []).forEach((teamRef) => {
      const teamId = toIdString(teamRef);
      if (teamId && !teamIds.has(teamId)) {
        pushIssue(
          issues,
          'error',
          'PLAYER_TEAM_OUT_OF_SCOPE',
          `Joueur ${playerName} : équipe ${teamId} absente du snapshot.`,
          { entityType: 'player', entityId: playerId }
        );
      }
    });

    (Array.isArray(player?.teamsSeason) ? player.teamsSeason : []).forEach((period) => {
      const teamId = toIdString(period?.team);
      if (teamId && !teamIds.has(teamId)) {
        pushIssue(
          issues,
          'error',
          'PLAYER_TEAM_SEASON_OUT_OF_SCOPE',
          `Joueur ${playerName} : teamsSeason référence l'équipe ${teamId}, absente du snapshot.`,
          { entityType: 'player', entityId: playerId }
        );
      }
    });
  });

  matches.forEach((match) => {
    const matchId = toIdString(match?._id);
    const competitionId = toIdString(match?.competition);
    const homeTeamId = toIdString(match?.homeTeam);
    const awayTeamId = toIdString(match?.awayTeam);

    if (!match?.dateTime || Number.isNaN(new Date(match.dateTime).getTime())) {
      pushIssue(issues, 'error', 'MATCH_DATETIME_INVALID', `Match ${matchId} : dateTime est absent ou invalide.`, { entityType: 'match', entityId: matchId });
    }

    if (!competitionId || !competitionIds.has(competitionId)) {
      pushIssue(issues, 'error', 'MATCH_COMPETITION_OUT_OF_SCOPE', `Match ${matchId} : compétition ${competitionId || 'absente'} absente du snapshot.`, { entityType: 'match', entityId: matchId });
    }

    [
      ['homeTeam', homeTeamId],
      ['awayTeam', awayTeamId],
    ].forEach(([field, teamId]) => {
      if (!teamId || !teamIds.has(teamId)) {
        pushIssue(issues, 'error', 'MATCH_TEAM_OUT_OF_SCOPE', `Match ${matchId} : ${field}=${teamId || 'absent'} absent du snapshot.`, { entityType: 'match', entityId: matchId });
      }
    });

    ['homeHolders', 'homeSubstitutes', 'awayHolders', 'awaySubstitutes'].forEach((field) => {
      (Array.isArray(match?.[field]) ? match[field] : []).forEach((entry, index) => {
        const playerId = toIdString(entry?._id);
        if (playerId && isMongoObjectId(playerId) && !playerIds.has(playerId)) {
          pushIssue(
            issues,
            'error',
            'MATCH_PLAYER_OUT_OF_SCOPE',
            `Match ${matchId} : ${field}[${index}]._id=${playerId} absent du snapshot.`,
            { entityType: 'match', entityId: matchId, field: `${field}[${index}]._id`, playerId }
          );
        }
      });
    });
  });

  const playerReferenceFields = ['player', 'scorer', 'buteur', 'droper', 'transformer', 'penaliter', 'passeur', 'inplayer', 'outplayer', 'shoter'];

  gameFacts.forEach((gameFact) => {
    const gameFactId = toIdString(gameFact?._id);
    const matchId = toIdString(gameFact?.idMatch);

    if (!matchId || !matchIds.has(matchId)) {
      pushIssue(
        issues,
        'error',
        'GAMEFACT_MATCH_OUT_OF_SCOPE',
        `GameFact ${gameFactId || gameFact?.type || ''} : idMatch=${matchId || 'absent'} ne pointe pas vers un match du snapshot.`,
        { entityType: 'gameFact', entityId: gameFactId }
      );
    }

    const detail = gameFact?.detail || {};
    const detailTeamId = toIdString(detail?.team);
    if (detailTeamId && isMongoObjectId(detailTeamId) && !teamIds.has(detailTeamId)) {
      pushIssue(issues, 'error', 'GAMEFACT_TEAM_OUT_OF_SCOPE', `GameFact ${gameFactId} : detail.team=${detailTeamId} absent du snapshot.`, { entityType: 'gameFact', entityId: gameFactId });
    }

    playerReferenceFields.forEach((field) => {
      const playerId = toIdString(detail?.[field]);
      if (playerId && isMongoObjectId(playerId) && !playerIds.has(playerId)) {
        pushIssue(issues, 'error', 'GAMEFACT_PLAYER_OUT_OF_SCOPE', `GameFact ${gameFactId} : detail.${field}=${playerId} absent du snapshot.`, { entityType: 'gameFact', entityId: gameFactId });
      }
    });
  });

  const errors = issues.filter((issue) => issue.severity === 'error');
  const warnings = issues.filter((issue) => issue.severity === 'warning');

  return {
    valid: errors.length === 0,
    issues,
    errors,
    warnings,
  };
};
